// eslint-disable-next-line import/no-unresolved
import { Box, RuleConfirmation, Spinner } from "@ui-kit/components/index.es";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { arrayOf, bool, func, shape, string } from "prop-types";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { actions as echeqRuleActions, selectors as echeqRuleSelectors } from "reducers/echeqRule";
import { getAccountDescriptionLimited } from "util/accounts";
import { getAccountNumber, getCurrencyIso, getProductType } from "util/cbuCmf";
import * as i18n from "util/i18n";

import moment from "moment-timezone";
import { selectors } from "reducers/echeqRules";
import { selectors as frequentDestinationSelectors } from "reducers/frequentDestination";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import isTokenActive from "util/token";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

const CreateEcheqRuleConfirmation = (props) => {
    const {
        dispatch,
        accountList,
        conditionsList,
        subConditionsIssuerList,
        SubConditionsAmountList,
        subConditionsReceivedList,
        initialValues,
        isDesktop,
        fetching,
        fetchingToken,
        tokenNumber,
        actionType,
        summarySelectedRule: { selectedRule },
        embeddedInV3Shell,
    } = props;

    useEffect(() => {
        if (!initialValues?.action || !initialValues?.name) {
            dispatch(push("/v2/echeqRules/create"));
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
        if (!actionType) {
            dispatch(push("/v2/echeqRules"));
        }
        // No limpiamos initialValues en el unmount: en mobile (shell v3 off) la confirmación
        // sufre un unmount/remount transitorio durante la generación del soft-token y el clean
        // vaciaba la regla en vuelo (action/account/name) -> NPE en backend. La limpieza se hace
        // al volver al listado (EcheqRulesMain dispara echeqRuleCleanData en su mount).
    }, []);

    useEffect(() => {
        const headerTitle = `${i18n.get(`manage.echeq.rules.${actionType}.title.confirmation`)} ${initialValues?.name ||
            ""}`;
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["echeqsRule"],
            headerTitle,
            goBackPath: "/v2/echeqRules/create",
        });
        return () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, actionType, initialValues?.name]);

    const handleBack = () => {
        dispatch(push("/v2/echeqRules/create"));
    };

    const getToken = async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    };

    const getAccountFromCBU = (cbu) => {
        const numberCbu = getAccountNumber(cbu);
        const productType = getProductType(cbu);
        const currency = getCurrencyIso(cbu);
        return getAccountDescriptionLimited({ number: numberCbu, productType, currency });
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqsRule" />}
            {(!embeddedInV3Shell || isDesktop) && (
                <Box className="admin-detail-head" px={48}>
                    {isDesktop ? (
                        <>
                            <Head onBack={handleBack} />
                            <Head
                                titleText={`${i18n.get(
                                    `manage.echeq.rules.${actionType}.title.confirmation`,
                                )} ${initialValues?.name || ""}`}
                                {...(actionType === "modify" && {
                                    messageTooltip: "echeqs.rule.actionConfirmation.tooltip",
                                })}
                                alwaysShowMessage
                            />
                        </>
                    ) : (
                        <Head
                            onBack={!isDesktop ? handleBack : null}
                            titleText={`${i18n.get(
                                `manage.echeq.rules.${actionType}.title.confirmation`,
                            )} ${initialValues?.name || ""}`}
                            titleClassName={!isDesktop ? "header-mobile-title-background-blue" : ""}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            {...(actionType === "modify" && {
                                messageTooltip: "echeqs.rule.actionConfirmation.tooltip",
                            })}
                            hideNavbarInMobile
                        />
                    )}
                </Box>
            )}
            {fetching || fetchingToken ? (
                <Box flex justify="center" align="center" column py={72}>
                    <Spinner size="large" />
                </Box>
            ) : (
                <Box px={isDesktop ? 48 : 16} pt={12}>
                    <RuleConfirmation
                        accountsOptions={accountList}
                        conditionsOptions={conditionsList}
                        confirmationProps={{
                            confirmationText: i18n.get("form.credential.hint"),
                            handleConfirm: ({ otp }) => {
                                const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
                                dispatch(echeqRuleActions.setGoBackEcheqsRule(true));
                                dispatch(
                                    echeqRuleActions.echeqRuleSend({
                                        ...initialValues,
                                        numberRule:
                                            initialValues?.numberRule ??
                                            (actionType === "modify" ? selectedRule?.idRule : undefined),
                                        _otp: finalOtp,
                                        actionType,
                                    }),
                                );
                            },

                            initialValue: {
                                otp: "",
                            },
                            loading: fetching || fetchingToken,
                            getToken,
                        }}
                        getCardLabel={(id) => {
                            const cardLabels = {
                                creationDate: i18n.get("echeqs.rule.list.card.creationDate.label"),
                                updatedDate: i18n.get("echeqs.rule.list.card.updatedDate.label"),
                                name: i18n.get("manage.echeq.rules.create.label.name"),
                                number: i18n.get("echeqs.rule.list.card.number.label"),
                                action: i18n.get("echeqs.rule.list.card.action.label"),
                                account: i18n.get("manage.echeq.rules.create.label.account"),
                                conditions: i18n.get("echeqs.rule.list.card.conditions.label"),
                                status: i18n.get("manage.echeq.rules.create.label.status"),
                                beneficiaryAddress: i18n.get("echeqs.rule.list.card.beneficiaryAddress.label"),
                                beneficiary: i18n.get("echeqs.rule.list.card.beneficiary.label"),
                                reference: i18n.get("echeqs.rule.list.card.reference.label"),
                                referenceValue: i18n.get("echeqs.rule.list.card.referenceValue.label"),
                            };
                            return cardLabels[id];
                        }}
                        headText={i18n.get("echeqs.rule.actionConfirmation.subtitle")}
                        operatorAmountOptions={SubConditionsAmountList}
                        operatorIssuerOptions={subConditionsIssuerList}
                        operatorReceivedOptions={subConditionsReceivedList}
                        rule={{
                            action: initialValues?.action?.value,
                            conditions: initialValues?.conditions,
                            name: initialValues?.name,
                            creationDate:
                                actionType === "modify" && selectedRule?.creationDate
                                    ? moment(selectedRule?.creationDate).tz("America/Argentina/Buenos_Aires")
                                    : moment(new Date()).tz("America/Argentina/Buenos_Aires"),
                            updatedDate:
                                actionType === "modify"
                                    ? moment(new Date()).tz("America/Argentina/Buenos_Aires")
                                    : null,
                            number: actionType === "modify" ? selectedRule?.idRule : null,
                            ...(initialValues?.account && { account: getAccountFromCBU(initialValues?.account) }),
                            currency: initialValues?.currency,
                            beneficiaryAddress: initialValues?.address,
                            beneficiary:
                                initialValues?.action?.value === "DISCOUNT"
                                    ? { name: i18n.get("global.bankName") }
                                    : initialValues?.beneficiary,
                            reference: initialValues?.reference,
                            referenceValue: initialValues?.referenceValue,
                        }}
                        drawerConditionsText={(ruleName) =>
                            i18n.get("echeqs.rule.list.card.context.conditions.description", "", {
                                RULE_NAME: ruleName,
                            })
                        }
                    />
                </Box>
            )}
        </>
    );
};
CreateEcheqRuleConfirmation.propTypes = {
    dispatch: func.isRequired,
    accountList: arrayOf(shape({})).isRequired,
    conditionsList: arrayOf(shape({})).isRequired,
    subConditionsIssuerList: arrayOf(shape({})).isRequired,
    SubConditionsAmountList: arrayOf(shape({})).isRequired,
    subConditionsReceivedList: arrayOf(shape({})).isRequired,
    initialValues: shape({}).isRequired,
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
    fetchingToken: bool.isRequired,
    tokenNumber: string,
    actionType: string,
    summarySelectedRule: shape({}),
    embeddedInV3Shell: bool,
};
CreateEcheqRuleConfirmation.defaultProps = {
    tokenNumber: null,
    actionType: null,
    summarySelectedRule: {
        selectedRule: null,
    },
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    fetching: echeqRuleSelectors.getFetching(state),
    accountList: echeqRuleSelectors.getAccountList(state),
    daysBeforeList: echeqRuleSelectors.getDaysBeforeList(state),
    actionsList: echeqRuleSelectors.getActionsList(state),
    conditionsList: echeqRuleSelectors.getConditionsList(state),
    subConditionsIssuerList: echeqRuleSelectors.getSubConditionsIssuerList(state),
    SubConditionsAmountList: echeqRuleSelectors.getSubConditionsAmountList(state),
    subConditionsReceivedList: echeqRuleSelectors.getSubConditionsReceivedList(state),
    fetchingFrequentDestinations: frequentDestinationSelectors.getFetching(state),
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
    listFrequentDestinations: frequentDestinationSelectors.getListFrequentDestinations(state),
    initialValues: echeqRuleSelectors.getInitialValues(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    actionType: echeqRuleSelectors.getActionType(state),
    summarySelectedRule: selectors.getSummarySelectedRule(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(CreateEcheqRuleConfirmation);
